import React, { useCallback, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { useAssignmentAuthoring } from "./AssignmentAuthoringProvider";
import { SECTION_TEMPLATES } from "./AddSectionDialog";
import {
  SectionEditorProvider,
  useSectionEditor,
} from "./sections/SectionEditorContext";
import { getSectionEditorComponent } from "./sections/sectionEditorRegistry";

const SECTION_TEMPLATE_OPTIONS = SECTION_TEMPLATES.map((template) => ({
  value: template.id,
  label: template.title,
  createSection: template.createSection,
}));

const getSectionSummary = (section) => {
  if (!section || typeof section !== "object") {
    return "No content configured yet.";
  }

  const { metadata = {} } = section;
  if (metadata == null) {
    return "No content configured yet.";
  }

  const candidateFields = [
    metadata.prompt,
    metadata.description,
    metadata.question,
    metadata.text,
    metadata.title,
    metadata.summary,
  ].filter((value) => typeof value === "string" && value.trim().length > 0);

  if (candidateFields.length > 0) {
    const summary = candidateFields[0].trim();
    if (summary.length > 140) {
      return `${summary.slice(0, 137)}…`;
    }

    return summary;
  }

  const metadataKeys = Object.keys(metadata || {});
  if (metadataKeys.length > 0) {
    try {
      return JSON.stringify(metadata, null, 2);
    } catch (error) {
      return metadataKeys.join(", ");
    }
  }

  return "No content configured yet.";
};

const getTemplateOption = (templateId) => {
  if (!templateId) {
    return null;
  }

  return (
    SECTION_TEMPLATE_OPTIONS.find((option) => option.value === templateId) || null
  );
};

const SectionEditorErrors = ({ errors }) => {
  if (!errors) {
    return null;
  }

  if (typeof errors === "string") {
    return <Alert severity="error">{errors}</Alert>;
  }

  if (Array.isArray(errors)) {
    if (errors.length === 0) {
      return null;
    }

    return (
      <Alert severity="error">
        <Stack component="ul" spacing={0.5} sx={{ pl: 2, my: 0 }}>
          {errors.map((error, index) => (
            <Box component="li" key={index} sx={{ typography: "body2" }}>
              {error}
            </Box>
          ))}
        </Stack>
      </Alert>
    );
  }

  if (typeof errors === "object") {
    const entries = Object.entries(errors).filter(([, value]) => value != null);

    if (entries.length === 0) {
      return null;
    }

    return (
      <Alert severity="error">
        <Stack spacing={0.75}>
          {entries.map(([key, value]) => (
            <Box key={key}>
              <Typography variant="subtitle2">{key}</Typography>
              <Typography variant="body2" color="text.secondary">
                {typeof value === "string" ? value : JSON.stringify(value)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Alert>
    );
  }

  return null;
};

const SectionCardContent = ({
  section: providedSection,
  onDuplicate,
  onDelete,
  dragHandleProps,
  isCollapsed = false,
  onToggleCollapse,
  onPreview,
}) => {
  const {
    section,
    setSectionTitle,
    setSectionTemplate,
    updateMetadata,
    sectionErrors,
  } = useSectionEditor();

  const summary = useMemo(
    () => getSectionSummary(providedSection || section),
    [providedSection, section]
  );

  const handleToggleCollapse = useCallback(() => {
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  }, [onToggleCollapse]);

  const handlePreview = useCallback(() => {
    if (onPreview) {
      onPreview();
    }
  }, [onPreview]);

  const templateOption = getTemplateOption(section.template);
  const SectionEditorComponent = getSectionEditorComponent(section.template);
  const availableTemplateOptions = useMemo(() => {
    if (templateOption || !section.template) {
      return SECTION_TEMPLATE_OPTIONS;
    }

    return [
      ...SECTION_TEMPLATE_OPTIONS,
      {
        value: section.template,
        label: section.template,
        createSection: () => ({
          title: section.title || "New section",
          metadata: section.metadata || {},
        }),
      },
    ];
  }, [section.template, section.title, section.metadata, templateOption]);

  const handleTitleChange = (event) => {
    setSectionTitle(event.target.value);
  };

  const handleTemplateChange = (event) => {
    const nextTemplate = event.target.value;
    if (!nextTemplate || nextTemplate === section.template) {
      setSectionTemplate(nextTemplate);
      return;
    }

    setSectionTemplate(nextTemplate);

    const option = getTemplateOption(nextTemplate);
    if (option && typeof option.createSection === "function") {
      const sectionDefaults = option.createSection();
      if (sectionDefaults && sectionDefaults.metadata) {
        updateMetadata(sectionDefaults.metadata);
      } else {
        updateMetadata({});
      }
      if (sectionDefaults && sectionDefaults.title && !section.title) {
        setSectionTitle(sectionDefaults.title);
      }
    } else {
      updateMetadata({});
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          {...dragHandleProps}
          sx={{
            display: "flex",
            alignItems: "center",
            color: "text.disabled",
            pt: 0.75,
          }}
          aria-label={`Reorder ${section.title}`}
        >
          <DragIndicatorIcon />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Stack spacing={2}>
            <TextField
              label="Section title"
              value={section.title}
              onChange={handleTitleChange}
              fullWidth
            />
            <TextField
              select
              label="Section type"
              value={section.template || ""}
              onChange={handleTemplateChange}
              helperText={
                templateOption
                  ? `Using the ${templateOption.label} template.`
                  : "Select the type of section to configure."
              }
              fullWidth
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">
                <em>Select template</em>
              </MenuItem>
              {availableTemplateOptions.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={isCollapsed ? "Expand section" : "Collapse section"}>
            <IconButton
              aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${
                section.title
              }`}
              onClick={handleToggleCollapse}
              size="small"
            >
              {isCollapsed ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ExpandLessIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Preview section">
            <IconButton
              aria-label={`Preview ${section.title}`}
              onClick={handlePreview}
              size="small"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label={`Duplicate ${section.title}`}
            onClick={onDuplicate}
            size="small"
          >
            <FileCopyIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label={`Delete ${section.title}`}
            onClick={onDelete}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 2 }} />
        <SectionEditorErrors errors={sectionErrors} />
        {SectionEditorComponent ? (
          <SectionEditorComponent />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select a section type to configure the assignment details.
          </Typography>
        )}
      </Collapse>
      {isCollapsed && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Summary
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {summary}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

const SectionCard = ({
  section,
  dragHandleProps,
  draggableProps,
  innerRef,
  onDuplicate,
  onDelete,
  isCollapsed = false,
  onToggleCollapse,
  onPreview,
}) => (
  <Card
    variant="outlined"
    ref={innerRef}
    {...draggableProps}
    data-testid="assignment-section-card"
    sx={{ borderRadius: 2 }}
    id={`assignment-section-${section.id}`}
  >
    <Box sx={{ p: 2.5 }}>
      <SectionEditorProvider sectionId={section.id}>
        <SectionCardContent
          section={section}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          dragHandleProps={dragHandleProps}
          isCollapsed={isCollapsed}
          onToggleCollapse={onToggleCollapse}
          onPreview={onPreview}
        />
      </SectionEditorProvider>
    </Box>
  </Card>
);

const SectionList = ({
  onAddSection,
  collapsedSections = {},
  onToggleSectionCollapse = () => {},
  onPreviewSection = () => {},
  activeSectionId = null,
}) => {
  const { sections, duplicateSection, deleteSection, reorderSections } =
    useAssignmentAuthoring();

  const handleDragEnd = useCallback(
    (result) => {
      if (!result?.destination) {
        return;
      }

      const { source, destination } = result;
      if (source.index === destination.index) {
        return;
      }

      reorderSections(source.index, destination.index);
    },
    [reorderSections]
  );

  const activeSection = useMemo(
    () => sections.find((section) => section.id === activeSectionId) || null,
    [sections, activeSectionId]
  );

  const hasSections = sections.length > 0;

  const displayedSections = useMemo(() => {
    if (activeSection) {
      return [activeSection];
    }

    return [];
  }, [activeSection]);

  const getSectionIndex = useCallback(
    (sectionId) => sections.findIndex((section) => section.id === sectionId),
    [sections]
  );

  return (
    <Stack spacing={2} data-testid="assignment-section-list">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" component="h2">
          Workspace
        </Typography>
        <Button variant="outlined" onClick={onAddSection}>
          Add Section
        </Button>
      </Stack>
      {!hasSections ? (
        <Box
          sx={{
            border: (theme) => `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            color: "text.secondary",
          }}
        >
          Use the Add Section button to start building your assignment.
        </Box>
      ) : !activeSection ? (
        <Box
          sx={{
            border: (theme) => `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            color: "text.secondary",
          }}
        >
          Select a section from the sidebar to edit its content.
        </Box>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="assignment-section-list">
            {(provided) => (
              <Stack spacing={2} ref={provided.innerRef} {...provided.droppableProps}>
                {displayedSections.map((section) => {
                  const index = getSectionIndex(section.id);
                  const draggableIndex = index === -1 ? 0 : index;
                  return (
                    <Draggable
                      key={section.id}
                      draggableId={section.id}
                      index={draggableIndex}
                    >
                      {(draggableProvided) => (
                        <SectionCard
                          section={section}
                          dragHandleProps={draggableProvided.dragHandleProps}
                          draggableProps={draggableProvided.draggableProps}
                          innerRef={draggableProvided.innerRef}
                          onDuplicate={() => duplicateSection(section.id)}
                          onDelete={() => deleteSection(section.id)}
                          isCollapsed={!!collapsedSections[section.id]}
                          onToggleCollapse={() =>
                            onToggleSectionCollapse(section.id)
                          }
                          onPreview={() => onPreviewSection(section.id)}
                        />
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Stack>
  );
};

export default SectionList;
